if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.initPersonalisationFields();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (new URL(window.location.href).searchParams.has('hf_edit')) {
          this.handleErrorMessage(window.hfJourneyStrings?.editLoading || 'Your basket item is loading. Please wait before saving.');
          return;
        }
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (!this.validatePersonalisation()) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.removeBlankOptionalPersonalisationProperties(formData);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings?.error || 'There was an error adding this item. Please try again.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      initPersonalisationFields() {
        this.personalisationInputs = Array.from(this.form.querySelectorAll('[data-personalisation-input]'));
        this.personalisationRequiredFields = Array.from(this.form.querySelectorAll('[data-personalisation-required]'));
        this.personalisationConfirmations = Array.from(
          this.form.querySelectorAll('[data-personalisation-confirmation]')
        );
        this.personalisationPhotoPreviews = new Map();
        this.personalisationComposingFields = new WeakSet();
        this.personalisationInlineErrors = new WeakMap();
        this.personalisationInlineErrorIndex = 0;
        // Approval is tied to the actual values, never just a checked checkbox.
        // Keep file references in memory only; no filenames or text go into storage.
        this.personalisationApprovals = new WeakMap();

        const controls = Array.from(
          new Set([...this.personalisationInputs, ...this.personalisationRequiredFields])
        );

        controls.forEach((field) => {
          this.normalisePersonalisationField(field);
          this.updatePersonalisationCounter(field);
          this.updatePersonalisationFileStatus(field);
          const updateControl = (event) => {
            if (event?.isComposing || this.personalisationComposingFields.has(field)) return;
            this.normalisePersonalisationField(field);
            this.syncPersonalisationControl(field);
            this.syncPersonalisationConfirmationState();
            this.clearPersonalisationError();
            this.updatePersonalisationCounter(field);
            this.updatePersonalisationFileStatus(field);
          };
          field.addEventListener('input', updateControl);
          field.addEventListener('change', updateControl);
          field.addEventListener('compositionstart', () => this.personalisationComposingFields.add(field));
          field.addEventListener('compositionend', () => {
            this.personalisationComposingFields.delete(field);
            updateControl();
          });
        });

        this.form.addEventListener('reset', () => {
          requestAnimationFrame(() => {
            this.personalisationApprovals = new WeakMap();
            this.personalisationConfirmations.forEach((confirmation) => {
              confirmation.checked = false;
              this.clearPersonalisationControl(confirmation);
            });
            controls
              .filter((field) => field.type === 'file')
              .forEach((field) => this.updatePersonalisationFileStatus(field));
            this.syncPersonalisationConfirmationState();
          });
        });

        this.personalisationConfirmations.forEach((confirmation) => {
          confirmation.addEventListener('change', () => {
            if (confirmation.checked && !confirmation.disabled) {
              this.personalisationApprovals.set(
                confirmation, this.capturePersonalisationApproval(confirmation)
              );
            } else {
              this.personalisationApprovals.delete(confirmation);
            }
            this.syncPersonalisationControl(confirmation);
            this.syncPersonalisationConfirmationState();
            this.clearPersonalisationError();
          });
        });

        this.syncPersonalisationConfirmationState();
      }

      capturePersonalisationApproval(confirmation) {
        const scope = confirmation.closest('[data-personalisation-field]') || this.form;
        const fields = Array.from(scope.querySelectorAll('[data-personalisation-input]'));
        fields.forEach((field) => {
          this.normalisePersonalisationField(field);
          this.updatePersonalisationCounter(field);
        });
        // Only actual personalisation controls belong to this approval. Quantity,
        // variant bookkeeping and hidden artwork-preview tokens are not included.
        return fields.map((field) => ({
          field,
          name: field.name,
          type: field.type,
          disabled: field.disabled,
          value: field.type === 'file' ? '' : field.value,
          checked: field.type === 'checkbox' || field.type === 'radio' ? field.checked : null,
          selected: field.multiple && field.options
            ? Array.from(field.selectedOptions, (option) => option.value).join('\u001f') : '',
          files: field.type === 'file' ? Array.from(field.files || []) : [],
          retainedPhoto: field.type === 'file' ? field.dataset.hfRetainedPhoto || '' : ''
        }));
      }

      personalisationApprovalMatches(confirmation) {
        const approved = this.personalisationApprovals.get(confirmation);
        if (!approved) return false;
        const current = this.capturePersonalisationApproval(confirmation);
        return approved.length === current.length && current.every((value, index) => {
          const previous = approved[index];
          return previous.field === value.field && previous.name === value.name
            && previous.type === value.type && previous.disabled === value.disabled
            && previous.value === value.value && previous.checked === value.checked
            && previous.retainedPhoto === value.retainedPhoto
            && previous.selected === value.selected && previous.files.length === value.files.length
            && value.files.every((file, fileIndex) => previous.files[fileIndex] === file);
        });
      }

      syncPersonalisationConfirmationState() {
        const spellingInputs = Array.from(
          this.form.querySelectorAll('[data-personalisation-spelling]')
        );
        const hasSpelling = spellingInputs.some((field) => this.hasPersonalisationValue(field));
        const hasPhoto = Array.from(
          this.form.querySelectorAll('[data-personalisation-input][type="file"]')
        ).some((field) => field.files?.length || field.dataset.hfRetainedPhoto);

        this.personalisationConfirmations.forEach((confirmation) => {
          const wrapper = confirmation.closest('[data-personalisation-confirmation-wrapper]');
          const active = confirmation.dataset.personalisationConfirmationMode === 'photo'
            ? true
            : hasSpelling || hasPhoto;
          confirmation.disabled = !active;
          confirmation.required = active;
          if (active) {
            if (confirmation.checked && !this.personalisationApprovalMatches(confirmation)) {
              confirmation.checked = false;
              this.personalisationApprovals.delete(confirmation);
              this.clearPersonalisationControl(confirmation);
            }
            confirmation.setAttribute('aria-required', 'true');
          } else {
            confirmation.checked = false;
            this.personalisationApprovals.delete(confirmation);
            confirmation.removeAttribute('aria-required');
            this.clearPersonalisationControl(confirmation);
          }
          if (wrapper) wrapper.hidden = !active;
        });

        return this.personalisationConfirmations.some((confirmation) => !confirmation.disabled);
      }

      validatePersonalisation({ includeConfirmation = true } = {}) {
        const requiredFields = Array.from(this.form.querySelectorAll('[data-personalisation-required]'));
        const personalisationInputs = Array.from(this.form.querySelectorAll('[data-personalisation-input]'));
        const fieldsToValidate = Array.from(new Set([...requiredFields, ...personalisationInputs]));
        if (fieldsToValidate.some((field) => this.personalisationComposingFields.has(field))) return false;
        // Normalize before validation, snapshots and FormData, including script-driven edits.
        fieldsToValidate.forEach((field) => {
          this.normalisePersonalisationField(field);
          this.updatePersonalisationCounter(field);
        });
        this.syncPersonalisationConfirmationState();
        const confirmations = includeConfirmation ? Array.from(
          this.form.querySelectorAll('[data-personalisation-confirmation]')
        ).filter((confirmation) => !confirmation.disabled)
          : [];

        const invalidContractField = this.form.querySelector('[data-personalisation-contract-invalid]');
        if (invalidContractField) {
          this.showPersonalisationError(
            invalidContractField,
            this.getPersonalisationContractError(invalidContractField)
          );
          return false;
        }

        const duplicatePropertyField = this.getDuplicatePersonalisationPropertyField(personalisationInputs);
        if (duplicatePropertyField) {
          this.showPersonalisationError(
            duplicatePropertyField,
            this.getPersonalisationContractError(duplicatePropertyField)
          );
          return false;
        }

        if (!fieldsToValidate.length && !confirmations.length) return true;

        for (const field of fieldsToValidate) {
          if (!field.hasAttribute('data-personalisation-required') && !this.hasPersonalisationValue(field)) {
            this.clearPersonalisationControl(field);
            continue;
          }

          const message = this.getPersonalisationFieldError(field);
          if (message) {
            this.showPersonalisationError(field, message);
            return false;
          }
          this.clearPersonalisationControl(field);
        }

        if (!includeConfirmation) {
          const confirmationOwnsError = this.form.querySelector(
            '[data-personalisation-confirmation][aria-invalid="true"]'
          );
          if (!confirmationOwnsError) this.handleErrorMessage();
          return true;
        }

        for (const confirmation of confirmations) {
          if (!confirmation.checked) {
            const message =
              confirmation.dataset.errorMessage ||
              'Please confirm your personalisation details before adding this item to your basket.';
            this.showPersonalisationError(confirmation, message);
            return false;
          }
          this.clearPersonalisationControl(confirmation);
        }

        this.clearPersonalisationError(true);
        return true;
      }

      getPersonalisationFieldError(field) {
        if (!this.hasPersonalisationValue(field)) {
          return (
            field.dataset.errorMessage ||
            'Please add your personalisation details before adding this item to your basket.'
          );
        }

        if (field.validity && field.validity.patternMismatch) {
          return field.dataset.patternMessage || field.validationMessage;
        }

        if (
          field.dataset.personalisationType === 'number' &&
          field.value &&
          /\D/.test(field.value)
        ) {
          return field.dataset.patternMessage || field.validationMessage;
        }

        if (field.dataset.personalisationCase === 'avoid-uppercase') {
          const letters = Array.from(field.value || '').filter((letter) => letter.toUpperCase() !== letter.toLowerCase());
          if (letters.length > 1 && letters.every((letter) => letter === letter.toUpperCase())) {
            return field.dataset.caseMessage || 'Please use upper and lower case letters for this personalisation.';
          }
        }

        if (
          field.type === 'file' &&
          field.files?.[0] &&
          !this.fileMatchesAccept(field.files[0], field.getAttribute('accept'))
        ) {
          return field.dataset.fileTypeMessage || field.dataset.errorMessage;
        }

        const maxLength = Number(field.dataset.personalisationMaxlength || field.getAttribute('maxlength')) || 0;
        if (field.type !== 'file' && maxLength > 0 && field.value.length > maxLength) {
          return field.dataset.maxlengthMessage ||
            `Please use no more than ${maxLength} characters, including spaces and punctuation.`;
        }

        const minLength = Number(field.dataset.personalisationMinlength || field.getAttribute('minlength')) || 0;
        if (minLength > 0 && field.value && field.value.trim().length < minLength) {
          return field.dataset.minlengthMessage || field.validationMessage || field.dataset.errorMessage;
        }

        return '';
      }

      getPersonalisationContractError(control) {
        return (
          control.closest('[data-personalisation-field]')?.dataset.personalisationContractError ||
          'This product’s personalisation options need attention. Please contact us before ordering.'
        );
      }

      getDuplicatePersonalisationPropertyField(fields = []) {
        const propertyNames = new Set();

        for (const field of fields) {
          if (!field.name || !field.name.startsWith('properties[')) continue;
          if (propertyNames.has(field.name)) return field;
          propertyNames.add(field.name);
        }

        return null;
      }

      fileMatchesAccept(file, accept) {
        if (!file || !accept) return true;

        const acceptedTypes = accept
          .split(',')
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean);
        if (!acceptedTypes.length) return true;

        const fileName = (file.name || '').toLowerCase();
        const mimeType = (file.type || '').toLowerCase();

        return acceptedTypes.some((acceptedType) => {
          if (acceptedType.startsWith('.')) return fileName.endsWith(acceptedType);
          if (acceptedType.endsWith('/*')) return mimeType.startsWith(acceptedType.slice(0, -1));
          return mimeType === acceptedType;
        });
      }

      removeBlankOptionalPersonalisationProperties(formData) {
        const optionalFields = Array.from(
          this.form.querySelectorAll('[data-personalisation-input]:not([data-personalisation-required])')
        );

        for (const field of optionalFields) {
          if (!field.name || this.hasPersonalisationValue(field)) continue;

          const sameNameFields = Array.from(this.form.elements).filter(
            (candidate) => candidate !== field && candidate.name === field.name
          );
          if (sameNameFields.some((candidate) => this.hasPersonalisationValue(candidate))) continue;

          formData.delete(field.name);
        }
      }

      normalisePersonalisationField(field) {
        if (!field || field.type === 'file' || this.personalisationComposingFields.has(field)) return;
        const numeric = field.dataset.personalisationType === 'number';
        const uppercase = field.dataset.personalisationCase === 'uppercase';
        if (!numeric && !uppercase) return;

        const normalise = (value) => {
          const cleaned = numeric ? value.replace(/\D/g, '') : value;
          return uppercase ? cleaned.toUpperCase() : cleaned;
        };
        const previousValue = field.value;
        const cleanedValue = normalise(previousValue);
        if (previousValue === cleanedValue) return;
        const start = field.selectionStart;
        const end = field.selectionEnd;
        const direction = field.selectionDirection;
        // Keep an expanded uppercase value intact so the length validator can explain it.
        field.value = cleanedValue;
        if (typeof start === 'number' && typeof end === 'number' && field.setSelectionRange) {
          field.setSelectionRange(
            normalise(previousValue.slice(0, start)).length,
            normalise(previousValue.slice(0, end)).length,
            direction || 'none'
          );
        }
      }

      hasPersonalisationValue(field) {
        if (!field) return true;
        if (field.type === 'file') return field.files && field.files.length > 0;
        if (field.type === 'checkbox' || field.type === 'radio') return field.checked;
        return Boolean(field.value && field.value.trim());
      }

      showPersonalisationError(control, message) {
        const disclosure = control.closest('details.hf-personalisation-field__optional');
        if (disclosure) disclosure.open = true;
        this.setPersonalisationControlError(control, message);
        control.focus({ preventScroll: true });
        const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        control.scrollIntoView({ behavior, block: 'center' });
      }

      setPersonalisationControlError(control, message) {
        if (this.personalisationErrorControl && this.personalisationErrorControl !== control) {
          this.clearPersonalisationControl(this.personalisationErrorControl);
        }

        const item = control.closest('[data-personalisation-item]');
        const wrapper = control.closest('[data-personalisation-field]');
        control.setCustomValidity(message);
        control.setAttribute('aria-invalid', 'true');
        if (item) item.classList.add('is-invalid');
        if (wrapper) wrapper.classList.add('is-invalid');
        this.personalisationErrorControl = control;
        this.handleErrorMessage(message);

        let inlineError = this.personalisationInlineErrors.get(control);
        if (!inlineError) {
          inlineError = document.createElement('p');
          inlineError.className = 'hf-personalisation-field__error';
          inlineError.id = `${control.id || this.form.id || 'Personalisation'}-error-${++this.personalisationInlineErrorIndex}`;
          const anchor = control.closest('[data-personalisation-confirmation-wrapper]') || control;
          anchor.after(inlineError);
          this.personalisationInlineErrors.set(control, inlineError);
        }
        inlineError.textContent = message;
        const descriptions = new Set((control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
        descriptions.add(inlineError.id);
        control.setAttribute('aria-describedby', Array.from(descriptions).join(' '));
        control.setAttribute('aria-errormessage', inlineError.id);
      }

      clearPersonalisationError(force = false) {
        const personalisationInputs = Array.from(this.form.querySelectorAll('[data-personalisation-input]'));
        const confirmations = Array.from(
          this.form.querySelectorAll('[data-personalisation-confirmation]')
        ).filter((confirmation) => !confirmation.disabled);
        const fieldsValid = personalisationInputs.every((field) => {
          const shouldValidate = field.hasAttribute('data-personalisation-required') || this.hasPersonalisationValue(field);
          return !shouldValidate || !this.getPersonalisationFieldError(field);
        });
        const confirmationsValid = confirmations.every((confirmation) => confirmation.checked);

        if (!force && (!fieldsValid || !confirmationsValid)) return;

        [...personalisationInputs, ...confirmations].forEach((control) => this.clearPersonalisationControl(control));
        this.handleErrorMessage();
      }

      syncPersonalisationControl(control) {
        const shouldValidate = control.required || control.hasAttribute('data-personalisation-required') || this.hasPersonalisationValue(control);
        const message = shouldValidate ? this.getPersonalisationFieldError(control) : '';
        if (!message) {
          const ownsError = this.personalisationErrorControl === control;
          this.clearPersonalisationControl(control);
          if (ownsError) this.handleErrorMessage();
          return;
        }

        this.setPersonalisationControlError(control, message);
      }

      clearPersonalisationControl(control) {
        const item = control.closest('[data-personalisation-item]');
        const wrapper = control.closest('[data-personalisation-field]');
        control.setCustomValidity('');
        control.removeAttribute('aria-invalid');
        control.removeAttribute('aria-errormessage');
        const inlineError = this.personalisationInlineErrors.get(control);
        if (inlineError) {
          const descriptions = (control.getAttribute('aria-describedby') || '').split(/\s+/)
            .filter((id) => id && id !== inlineError.id);
          if (descriptions.length) control.setAttribute('aria-describedby', descriptions.join(' '));
          else control.removeAttribute('aria-describedby');
          inlineError.remove();
          this.personalisationInlineErrors.delete(control);
        }
        if (item) item.classList.remove('is-invalid');
        if (wrapper && !wrapper.querySelector('[aria-invalid="true"]')) wrapper.classList.remove('is-invalid');
        if (this.personalisationErrorControl === control) this.personalisationErrorControl = null;
      }

      updatePersonalisationFileStatus(field) {
        if (!field || field.type !== 'file') return false;

        const item = field.closest('[data-personalisation-item]') || field.closest('[data-personalisation-field]');
        const status = item?.querySelector('[data-personalisation-file-status]');
        if (!status) return false;

        const file = field.files?.[0] || null;
        const empty = status.dataset.emptyText || '';
        const prefix = status.dataset.selectedPrefix || '';
        const replace = status.dataset.replaceText || '';
        const retained = field.dataset.hfRetainedPhoto || '';
        const retainedText = window.hfJourneyStrings?.photoRetained
          || item?.querySelector('.hf-pdp-edit-file-note')?.textContent.trim() || '';
        const text = file ? [prefix, file.name, replace].filter(Boolean).join(' ') : retained ? retainedText : empty;
        this.updatePhotoReview(field, file, retained);
        if (status.textContent === text) {
          return false;
        }
        status.textContent = text;
        return true;
      }

      updatePhotoReview(field, file, retained) {
        let state = this.personalisationPhotoPreviews.get(field);
        if (!state) {
          const wrap = document.createElement('div');
          wrap.className = 'hf-photo-preview';
          const img = document.createElement('img');
          const label = this.form.querySelector(`label[for="${CSS.escape(field.id)}"]`)?.textContent.trim() || '';
          img.alt = `${window.hfJourneyStrings?.photoPreview || ''}: ${label}`;
          const actions = document.createElement('div');
          actions.className = 'hf-photo-preview__actions';
          const replace = document.createElement('button');
          replace.type = 'button';
          replace.textContent = window.hfJourneyStrings?.photoReplace || '';
          replace.setAttribute('aria-label', `${replace.textContent}: ${label}`);
          replace.addEventListener('click', () => field.click());
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.textContent = window.hfJourneyStrings?.photoRemove || '';
          remove.setAttribute('aria-label', `${remove.textContent}: ${label}`);
          remove.addEventListener('click', () => {
            field.value = '';
            field.dispatchEvent(new Event('change', { bubbles: true }));
            field.focus();
          });
          actions.append(replace, remove);
          wrap.append(img, actions);
          field.after(wrap);
          state = { wrap, img, actions, file: null, objectUrl: '' };
          this.personalisationPhotoPreviews.set(field, state);
        }
        if (state.file !== file) {
          if (state.objectUrl) URL.revokeObjectURL(state.objectUrl);
          state.objectUrl = file && file.type.startsWith('image/') ? URL.createObjectURL(file) : '';
          state.file = file;
        }
        const src = state.objectUrl || (/^(https?:)?\/\//i.test(retained) || retained.startsWith('/uploads/') ? retained : '');
        state.wrap.hidden = !src;
        if (src && state.img.getAttribute('src') !== src) state.img.src = src;
        if (!src) state.img.removeAttribute('src');
        state.actions.hidden = field.disabled || !file;
        const files = this.personalisationInputs.filter((input) => input.type === 'file');
        const count = files.filter((input) => input.files?.length || input.dataset.hfRetainedPhoto).length;
        const progress = this.form.querySelector('[data-hf-photo-progress]');
        if (progress) {
          const text = progress.dataset.countTemplate.replace('__COUNT__', String(count)).replace('__TOTAL__', String(files.length));
          if (progress.textContent !== text) progress.textContent = text;
        }
      }

      disconnectedCallback() {
        this.personalisationPhotoPreviews?.forEach((state) => {
          if (state.objectUrl) URL.revokeObjectURL(state.objectUrl);
        });
      }

      updatePersonalisationCounter(field) {
        if (!field) return;

        const item = field.closest('[data-personalisation-item]');
        const fallbackWrapper = field.closest('[data-personalisation-field]');
        const counterScope = item || fallbackWrapper;
        const counter = counterScope ? counterScope.querySelector('[data-personalisation-counter]') : null;

        if (counter) {
          const max = Number(field.dataset.personalisationMaxlength || field.getAttribute('maxlength')) || 0;
          const current = field.value ? field.value.length : 0;
          counter.textContent = max ? `${current}/${max}` : current;
          counter.classList.toggle('is-near-limit', max > 0 && current >= max * 0.85);
        }

        if (field.hasAttribute('data-personalisation-preview-source') && fallbackWrapper) {
          const preview = fallbackWrapper.querySelector('[data-personalisation-preview]');
          if (preview) {
            const emptyText = fallbackWrapper.dataset.personalisationPreviewEmpty || '';
            preview.textContent = field.value.trim() || emptyText;
          }
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        this.errorMessage.textContent = errorMessage || '';
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
